import "./styles/main.css";
import { parseContainerMetadata } from "./audio/metadata";
import { buildReport } from "./report";
import type {
  AnalysisReport,
  AnalysisResult,
  ContainerMetadata,
  WorkerResponse,
} from "./types";
import { debounce, element } from "./utils/dom";
import {
  isPaletteName,
  populateFileHeader,
  renderHero,
  renderReport,
  renderSpectrogram,
  renderVisualization,
  renderWaveform,
  showCursorReadout,
  updateGradient,
  type VisualizationState,
} from "./visualization/renderer";

interface ApplicationState {
  audioBuffer: AudioBuffer | null;
  file: File | null;
  analysis: AnalysisResult | null;
  report: AnalysisReport | null;
  mix: Float32Array | null;
  metadata: ContainerMetadata | null;
  palette: VisualizationState["palette"];
  floorDb: number;
}

const state: ApplicationState = {
  audioBuffer: null,
  file: null,
  analysis: null,
  report: null,
  mix: null,
  metadata: null,
  palette: "ember",
  floorDb: -90,
};

initialize();

function initialize(): void {
  renderHero();
  bindEvents();
  window.addEventListener(
    "resize",
    debounce(() => {
      const visualization = getVisualizationState();
      if (visualization) {
        renderSpectrogram(visualization);
        renderWaveform(visualization.mix);
      }
    }, 120),
  );
}

function bindEvents(): void {
  const input = element<HTMLInputElement>("#fileInput");
  ["#openButton", "#heroOpenButton", "#replaceButton"].forEach((selector) => {
    element<HTMLButtonElement>(selector).addEventListener("click", () => input.click());
  });
  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file) void analyzeFile(file);
    input.value = "";
  });
  element("#demoButton").addEventListener("click", () => void analyzeDemo());
  element("#exportButton").addEventListener("click", exportReport);
  element<HTMLSelectElement>("#paletteSelect").addEventListener("change", ({ currentTarget }) => {
    const value = (currentTarget as HTMLSelectElement).value;
    if (!isPaletteName(value)) return;
    state.palette = value;
    const visualization = getVisualizationState();
    if (visualization) renderSpectrogram(visualization);
    updateGradient(value);
  });
  element<HTMLSelectElement>("#floorSelect").addEventListener("change", ({ currentTarget }) => {
    state.floorDb = Number((currentTarget as HTMLSelectElement).value);
    element("#floorLegend").textContent = `${state.floorDb} dB`.replace("-", "−");
    const visualization = getVisualizationState();
    if (visualization) renderSpectrogram(visualization);
  });

  let dragDepth = 0;
  document.addEventListener("dragenter", (event) => {
    event.preventDefault();
    dragDepth++;
    element("#dropOverlay").classList.remove("hidden");
  });
  document.addEventListener("dragover", (event) => event.preventDefault());
  document.addEventListener("dragleave", (event) => {
    event.preventDefault();
    dragDepth--;
    if (dragDepth <= 0) element("#dropOverlay").classList.add("hidden");
  });
  document.addEventListener("drop", (event) => {
    event.preventDefault();
    dragDepth = 0;
    element("#dropOverlay").classList.add("hidden");
    const file = event.dataTransfer?.files[0];
    if (file) void analyzeFile(file);
  });

  element("#spectrogramCanvas").addEventListener("mousemove", (event) => {
    if (state.audioBuffer) showCursorReadout(event as MouseEvent, state.audioBuffer);
  });
  element("#spectrogramCanvas").addEventListener("mouseleave", () => {
    element("#cursorReadout").classList.add("hidden");
  });
}

async function analyzeFile(file: File): Promise<void> {
  setLoading(true, "Decodificando áudio", "Lendo o container localmente…", 5);
  try {
    const arrayBuffer = await file.arrayBuffer();
    state.metadata = parseContainerMetadata(arrayBuffer, file.name);
    updateLoading("Decodificando áudio", "Convertendo para PCM no navegador…", 10);
    const context = new AudioContext();
    const audioBuffer = await context.decodeAudioData(arrayBuffer.slice(0));
    await context.close();
    state.file = file;
    state.audioBuffer = audioBuffer;
    state.mix = createMonoMix(audioBuffer);
    populateFileHeader(file, audioBuffer);
    updateLoading("Calculando FFT", "O Worker está mapeando as frequências…", 15);
    state.analysis = await runWorkerAnalysis(state.mix, audioBuffer);
    finishAnalysis();
  } catch (error) {
    console.error(error);
    setLoading(false);
    window.alert(
      "Não foi possível decodificar este arquivo. Tente FLAC, WAV, MP3, M4A, OGG ou Opus em uma versão recente do Chrome/Edge.",
    );
  }
}

async function analyzeDemo(): Promise<void> {
  setLoading(true, "Criando sinal de teste", "Gerando um falso lossless com cutoff em 16 kHz…", 8);
  const sampleRate = 44100;
  const duration = 18;
  const length = sampleRate * duration;
  const offline = new OfflineAudioContext(2, length, sampleRate);
  const noiseBuffer = offline.createBuffer(2, length, sampleRate);

  for (let channel = 0; channel < 2; channel++) {
    const data = noiseBuffer.getChannelData(channel);
    for (let index = 0; index < length; index++) {
      const time = index / sampleRate;
      data[index] =
        Math.sin(2 * Math.PI * (110 + 16 * Math.sin(time * 0.35)) * time) * 0.31 +
        Math.sin(2 * Math.PI * 880 * time) * 0.15 +
        Math.sin(2 * Math.PI * 4200 * time) * 0.08 +
        Math.sin(2 * Math.PI * 11800 * time) * 0.035 +
        (Math.random() * 2 - 1) * 0.035;
    }
  }

  const source = offline.createBufferSource();
  source.buffer = noiseBuffer;
  let tail: AudioNode = source;
  for (let index = 0; index < 5; index++) {
    const filter = offline.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 15800;
    filter.Q.value = 0.72;
    tail.connect(filter);
    tail = filter;
  }
  const gain = offline.createGain();
  gain.gain.value = 0.82;
  tail.connect(gain);
  gain.connect(offline.destination);
  source.start();
  const buffer = await offline.startRendering();
  state.audioBuffer = buffer;
  state.mix = createMonoMix(buffer);
  state.file = new File(
    [new Uint8Array(Math.round(length * 2 * 2 * 0.48))],
    "demo_master_24bit.flac",
    { type: "audio/flac" },
  );
  state.metadata = { container: "FLAC", bitDepth: 24, declaredLossless: true };
  populateFileHeader(state.file, buffer);
  state.analysis = await runWorkerAnalysis(state.mix, buffer);
  finishAnalysis();
}

function runWorkerAnalysis(mix: Float32Array, audioBuffer: AudioBuffer): Promise<AnalysisResult> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL("./audio/analyzer.worker.ts", import.meta.url), {
      type: "module",
    });
    worker.onmessage = ({ data }: MessageEvent<WorkerResponse>) => {
      if (data.type === "progress") {
        updateLoading("Calculando FFT", `${data.value}% · analisando energia espectral…`, data.value);
      } else {
        worker.terminate();
        resolve({
          ...data.result,
          spectrogram: new Uint8Array(data.result.spectrogram),
        });
      }
    };
    worker.onerror = (error) => {
      worker.terminate();
      reject(error);
    };
    const samples = mix.slice().buffer;
    worker.postMessage(
      {
        type: "analyze",
        samples,
        sampleRate: audioBuffer.sampleRate,
        duration: audioBuffer.duration,
        maxFrames: Math.min(1800, Math.max(700, Math.round(audioBuffer.duration * 10))),
        fftSize: audioBuffer.sampleRate >= 88200 ? 8192 : 4096,
      },
      [samples],
    );
  });
}

function finishAnalysis(): void {
  if (!state.analysis || !state.audioBuffer || !state.file || !state.metadata || !state.mix) return;
  updateLoading("Renderizando laudo", "Transformando os números em evidências…", 94);
  state.report = buildReport({
    analysis: state.analysis,
    file: state.file,
    duration: state.audioBuffer.duration,
    sampleRate: state.audioBuffer.sampleRate,
  });
  const visualization = getVisualizationState();
  if (!visualization) return;
  requestAnimationFrame(() => {
    element("#workspace").classList.remove("hidden");
    renderVisualization(visualization);
    renderReport(state.report!, state.metadata!);
    element<HTMLButtonElement>("#exportButton").disabled = false;
    setLoading(false);
    element("#workspace").scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function exportReport(): void {
  if (!state.report || !state.file || !state.audioBuffer || !state.metadata) return;
  const payload = {
    schemaVersion: 1,
    generatedAt: new Date().toISOString(),
    application: "Sonora Audio Forensics",
    file: {
      name: state.file.name,
      size: state.file.size,
      type: state.file.type,
      duration: state.audioBuffer.duration,
      sampleRate: state.audioBuffer.sampleRate,
      channels: state.audioBuffer.numberOfChannels,
      metadata: state.metadata,
    },
    report: state.report,
    disclaimer: "Heuristic evidence only; this report does not certify file provenance.",
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${state.file.name.replace(/\.[^.]+$/, "")}-sonora-report.json`;
  link.click();
  URL.revokeObjectURL(link.href);
}

function createMonoMix(audioBuffer: AudioBuffer): Float32Array {
  const mix = new Float32Array(audioBuffer.length);
  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const source = audioBuffer.getChannelData(channel);
    for (let index = 0; index < audioBuffer.length; index++) {
      mix[index] += source[index] / audioBuffer.numberOfChannels;
    }
  }
  return mix;
}

function getVisualizationState(): VisualizationState | null {
  if (!state.analysis || !state.mix || !state.audioBuffer || !state.file || !state.metadata) {
    return null;
  }
  return {
    analysis: state.analysis,
    mix: state.mix,
    audioBuffer: state.audioBuffer,
    file: state.file,
    metadata: state.metadata,
    palette: state.palette,
    floorDb: state.floorDb,
  };
}

function setLoading(
  visible: boolean,
  title = "",
  detail = "",
  progress = 0,
): void {
  element("#loadingOverlay").classList.toggle("hidden", !visible);
  if (visible) updateLoading(title, detail, progress);
}

function updateLoading(title: string, detail: string, progress: number): void {
  element("#loadingTitle").textContent = title;
  element("#loadingDetail").textContent = detail;
  element<HTMLElement>("#progressBar").style.width = `${progress}%`;
}

if ("serviceWorker" in navigator && location.protocol !== "file:") {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`);
  });
}
